import { checkinLines } from './checkin';
// Makes the nightly report PDF, stores it in the private "eod-reports" bucket and links it to the day.
import { supabase } from '@/src/lib/supabase';
import { viewFile } from '@/src/components/dashboard/pdf-pages';
import { buildEodPdf, type TeamNote, type HistoryDay, type PdfSummary } from '@/src/lib/eodPdf';
import { computeEod, sectionOn } from '@/src/lib/eod';
import type { EodConfig, EodData } from '@/src/lib/eod';

export async function makeEodPdf(p: {
  venueId: string; venueName: string; date: string; data: EodData; config: EodConfig; submittedBy: string; submittedAt: Date;
}): Promise<{ path?: string; error?: string }> {
  try {
    // The team's EOD notes for the same day go at the end of the report.
    const { data: rows } = await supabase.from('eod_reports').select('answers, users(name)').eq('venue_id', p.venueId).eq('report_date', p.date);
    const team: TeamNote[] = ((rows ?? []) as Array<{ answers: Record<string, unknown> | null; users: { name: string | null } | Array<{ name: string | null }> | null }>)
      .map((r) => ({ name: (Array.isArray(r.users) ? r.users[0]?.name : r.users?.name) ?? 'Team member', answers: Object.fromEntries(checkinLines(r.answers ?? {})) }))
      .filter((t) => Object.values(t.answers).some((v) => v?.trim()));
    // The two weeks before, for "vs last week" and the 7-day chart.
    const from = shiftIso(p.date, -13);
    const { data: hist } = await supabase.from('sales_days').select('trade_date, total_sales, covers, eod').eq('venue_id', p.venueId).gte('trade_date', from).lt('trade_date', p.date);
    const history: HistoryDay[] = ((hist ?? []) as Array<{ trade_date: string; total_sales: number | null; covers: number | null; eod: { calc?: { labourPct?: number | null } } | null }>)
      .map((r) => ({ date: r.trade_date, total: r.total_sales != null ? Number(r.total_sales) : null, covers: r.covers, labourPct: r.eod?.calc?.labourPct ?? null }));
    // Weather, if that day's briefing has it.
    let weather: string | null = null;
    try {
      const { data: b } = await supabase.from('venue_briefings').select('facts').eq('venue_id', p.venueId).eq('brief_date', p.date).maybeSingle();
      const w = (b?.facts as { weather?: { label: string; min: number; max: number } } | null)?.weather;
      if (w) weather = `${w.label.replace(/^./, (x) => x.toUpperCase())}, ${w.min}–${w.max}°C`;
    } catch { /* no weather */ }
    const summary = await aiSummary(p, team, history);
    const blob = await buildEodPdf({ ...p, team, history, summary, weather });
    const path = `${p.venueId}/${p.date}.pdf`;
    const { error: upErr } = await supabase.storage.from('eod-reports').upload(path, blob, { upsert: true, contentType: 'application/pdf' });
    if (upErr) return { error: upErr.message };
    await supabase.from('sales_days').update({ pdf_path: path }).eq('venue_id', p.venueId).eq('trade_date', p.date);
    return { path };
  } catch (e) {
    return { error: e instanceof Error ? e.message : 'Could not make the PDF.' };
  }
}

function shiftIso(iso: string, n: number): string {
  const d = new Date(`${iso}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10);
}

/** "What stood out / Watch tomorrow" for the top of the PDF. Best effort: the PDF is made without it if the AI is unavailable. */
async function aiSummary(p: { date: string; data: EodData; config: EodConfig }, team: TeamNote[], history: HistoryDay[]): Promise<PdfSummary | null> {
  try {
    const k = computeEod(p.data, p.config);
    const lastWeek = history.find((h) => h.date === shiftIso(p.date, -7)) ?? null;
    const on = (id: Parameters<typeof sectionOn>[1]) => sectionOn(p.config, id);
    const facts = {
      date: p.date, weekday: new Date(`${p.date}T12:00:00`).toLocaleDateString('en-AU', { weekday: 'long' }),
      tonight: { total_inc_gst: k.total, food: k.food, bev: k.bev, covers: k.covers, per_head: k.perHead, labour_pct: on('labour') ? k.labourPct : undefined, labour_cost: on('labour') ? k.labourTotal : undefined,
        cash_variance: on('cash') ? k.cashVariance : undefined, payments_variance: on('payments') ? k.paymentsVariance : undefined, discounts: on('discounts') ? k.discountsTotal : undefined, deleted: on('discounts') ? k.deletedAmount : undefined },
      by_service: p.config.services.length > 1 ? k.services : undefined,
      same_day_last_week: lastWeek, last_14_days: history.map((h) => ({ date: h.date, total: h.total, covers: h.covers })),
      targets: p.config.targets ?? undefined,
      variance_explanations: [p.data.cash.note, p.data.payments.note].filter((x) => x?.trim()),
      manager_notes: p.data.notes || undefined,
      service_notes: p.data.service ? Object.fromEntries(Object.entries(p.data.service).map(([s, x]) => [s, x.notes])) : undefined,
      team_check_ins: team.map((t) => ({ who: t.name.split(' ')[0], notes: t.answers })),
    };
    const { data, error } = await supabase.functions.invoke('eod-summary', { body: { facts, kind: 'nightly' } });
    if (error || !(data as { summary?: PdfSummary } | null)?.summary) return null;
    const sm = (data as { summary: PdfSummary }).summary;
    const teamRows = Array.isArray(sm.team) ? sm.team.filter((t) => t?.topic && t?.summary).slice(0, 10) : [];
    return { stood_out: (sm.stood_out ?? []).slice(0, 3), watch: (sm.watch ?? []).slice(0, 2), ...(team.length && teamRows.length ? { team: teamRows } : {}) };
  } catch { return null; }
}

export async function openEodPdf(path: string): Promise<string | null> {
  const { data, error } = await supabase.storage.from('eod-reports').createSignedUrl(path, 600);
  if (error || !data?.signedUrl) return error?.message ?? 'Could not open the PDF.';
  const day = path.split('/').pop()?.replace('.pdf', '') ?? '';
  // <venue>/weekly/<monday>.pdf is a weekly report; everything else is a nightly one.
  viewFile(data.signedUrl, `${path.includes('/weekly/') ? 'Weekly report week of' : 'Nightly report'} ${day}`.trim());
  return null;
}
